// Old containers implied direction in the TYPE; the replacements read it from an
// option — so a type-only swap silently flips a row into a column.
const STATIC_LAYOUT_DIRECTION = "static-layout-direction";
const LEGACY_LAYOUT_DIRECTION = "layout-direction";

const VERTICAL = "vertical";
const HORIZONTAL = "horizontal";

const WRAPPER_BY_DIRECTION = {
  [VERTICAL]: "VerticalContainerWrapper",
  [HORIZONTAL]: "HorizontalContainerWrapper",
};

// `Container` declared direction as an option, so it resolves per element.
const IMPLIED_DIRECTION = {
  VerticalContainer: VERTICAL,
  HorizontalContainer: HORIZONTAL,
  Container: null,
};

const LAYOUT_ALIGN = "layout-align";
const LAYOUT_ALIGN_VERTICAL = "layout-align-vertical";

// A ROW's `align` prop comes from layout-align-VERTICAL and its `vertical-align`
// from layout-align, so the horizontal pair maps CROSSED (Container.vue:17-18).
const ALIGN_RENAMES_BY_SOURCE = {
  HorizontalContainer: {
    "horizontal-layout-align": LAYOUT_ALIGN_VERTICAL,
    "horizontal-layout-vertical-align": LAYOUT_ALIGN,
  },
  VerticalContainer: {
    "vertical-layout-align": LAYOUT_ALIGN,
    "vertical-layout-vertical-align": LAYOUT_ALIGN_VERTICAL,
  },
  // Already on the modern keys and vocabulary — nothing to remap.
  Container: {},
};

// Old options stored concrete edges; the new pair stores axis-agnostic values that
// Container.vue resolves per direction. Distribution values pass through.
const ABSTRACT_ALIGN_VALUES = {
  left: "start",
  top: "start",
  right: "end",
  bottom: "end",
  center: "center",
  middle: "center",
};

function isConvertibleContainer(type) {
  return Object.prototype.hasOwnProperty.call(IMPLIED_DIRECTION, type);
}

function readLeafValue(leaf) {
  if (leaf && typeof leaf === "object") {
    return leaf.r;
  }
  return leaf;
}

// options[themeMode][breakpoint][state][key]; any level can be absent.
function findDirectionInTree(optionsTree) {
  for (const modeTree of Object.values(optionsTree ?? {})) {
    for (const breakpointTree of Object.values(modeTree ?? {})) {
      for (const stateTree of Object.values(breakpointTree ?? {})) {
        const value = readLeafValue(stateTree?.[STATIC_LAYOUT_DIRECTION])
          ?? readLeafValue(stateTree?.[LEGACY_LAYOUT_DIRECTION]);
        if (value === VERTICAL || value === HORIZONTAL) {
          return value;
        }
      }
    }
  }
  return null;
}

function resolveDirection(type, optionsTree) {
  return IMPLIED_DIRECTION[type] ?? findDirectionInTree(optionsTree) ?? VERTICAL;
}

function toAbstractAlignLeaf(leaf) {
  if (!leaf || typeof leaf !== "object") {
    return ABSTRACT_ALIGN_VALUES[leaf] ?? leaf;
  }
  const translated = { ...leaf };
  for (const field of ["r", "c"]) {
    if (typeof translated[field] === "string" && ABSTRACT_ALIGN_VALUES[translated[field]]) {
      translated[field] = ABSTRACT_ALIGN_VALUES[translated[field]];
    }
  }
  return translated;
}

// Every leaf carries over (the result must render identically); the align pair is
// re-keyed + re-vocabularied, and the superseded direction key dropped.
function convertOptionsTree(optionsTree, direction, sourceType) {
  const alignRenames = ALIGN_RENAMES_BY_SOURCE[sourceType] ?? {};
  const converted = {};
  for (const [mode, modeTree] of Object.entries(optionsTree ?? {})) {
    converted[mode] = {};
    for (const [breakpoint, breakpointTree] of Object.entries(modeTree ?? {})) {
      converted[mode][breakpoint] = {};
      for (const [state, stateTree] of Object.entries(breakpointTree ?? {})) {
        const carried = {};
        for (const [key, leaf] of Object.entries(stateTree ?? {})) {
          if (key === LEGACY_LAYOUT_DIRECTION || key === STATIC_LAYOUT_DIRECTION) {
            continue;
          }
          const alignKey = alignRenames[key];
          if (alignKey) {
            carried[alignKey] = toAbstractAlignLeaf(leaf);
            continue;
          }
          carried[key] = leaf;
        }
        // Only the default state gets the direction: a hover/active override of
        // the layout axis was never a thing, and writing one would invent state.
        if (state === "default") {
          carried[STATIC_LAYOUT_DIRECTION] = { r: direction, c: direction };
        }
        converted[mode][breakpoint][state] = carried;
      }
    }
  }
  return converted;
}

// Carried values layer over the TARGET's defaults so no expected key is missing;
// `strandedKeys` are carried keys it doesn't declare, for the caller to report.
function composeConvertedOptions(defaultsTree, carriedTree) {
  const composed = {};
  const strandedKeys = new Set();
  const modes = new Set([...Object.keys(defaultsTree ?? {}), ...Object.keys(carriedTree ?? {})]);
  for (const mode of modes) {
    const defaultsMode = defaultsTree?.[mode] ?? {};
    const carriedMode = carriedTree?.[mode] ?? {};
    composed[mode] = {};
    const breakpoints = new Set([...Object.keys(defaultsMode), ...Object.keys(carriedMode)]);
    for (const breakpoint of breakpoints) {
      const defaultsBreakpoint = defaultsMode[breakpoint] ?? {};
      const carriedBreakpoint = carriedMode[breakpoint] ?? {};
      composed[mode][breakpoint] = {};
      const states = new Set([...Object.keys(defaultsBreakpoint), ...Object.keys(carriedBreakpoint)]);
      for (const state of states) {
        const defaultsState = defaultsBreakpoint[state] ?? {};
        const carriedState = carriedBreakpoint[state] ?? {};
        for (const key of Object.keys(carriedState)) {
          if (!(key in defaultsState)) {
            strandedKeys.add(key);
          }
        }
        composed[mode][breakpoint][state] = { ...defaultsState, ...carriedState };
      }
    }
  }
  return { options: composed, strandedKeys: [...strandedKeys] };
}

// null when nothing to convert — callers gate the affordance on the same answer.
function planContainerConversion(type, optionsTree) {
  if (!isConvertibleContainer(type)) {
    return null;
  }
  const direction = resolveDirection(type, optionsTree);
  const targetType = WRAPPER_BY_DIRECTION[direction];
  if (targetType === type) {
    return null;
  }
  return {
    targetType,
    direction,
    options: convertOptionsTree(optionsTree, direction, type),
  };
}

// Never throws: a malformed tree renders as-is (the register fix still resolves
// the deprecated type), so this can't blank a render.
function normalizeDeprecatedContainerEntry(entry) {
  if (!entry || !isConvertibleContainer(entry.type)) {
    return entry;
  }
  try {
    const plan = planContainerConversion(entry.type, entry.options);
    if (!plan) {
      return entry;
    }
    return { ...entry, type: plan.targetType, options: plan.options };
  } catch {
    return entry;
  }
}

module.exports = {
  ABSTRACT_ALIGN_VALUES,
  ALIGN_RENAMES_BY_SOURCE,
  IMPLIED_DIRECTION,
  LEGACY_LAYOUT_DIRECTION,
  STATIC_LAYOUT_DIRECTION,
  WRAPPER_BY_DIRECTION,
  composeConvertedOptions,
  isConvertibleContainer,
  normalizeDeprecatedContainerEntry,
  planContainerConversion,
  resolveDirection,
};
